/**
 * @file billUnitConversion.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-13
 * @modified 2026-10-22
 * @description A bill and its PO line counted in different units (Jade,
 *              13 Oct 2026 — "Bill & PO Units Differ": "bill in tonnes with
 *              unit price per tonne, PO in 25kg bags, so: unit price /
 *              (1000/25 = 40) × PO qty supplied — just check totals match per
 *              line and VAT and net/gross totals").
 *
 *   THE CASE. A supplier often prices a bagged product per TONNE — 1.00 T at
 *   £400/T — while our PO line counts it in packs — 40 × 25kg bag at £10. The
 *   reverse happens too: Xero-made POs priced a 25kg-bag item per tonne (PO-19284
 *   prilled urea, 1.2 at £631) and the supplier billed bags (48 at £15.78).
 *   And some suppliers bill per KILOGRAM: Lacto Production invoices
 *   "2 000,000 KG at 2,015 = 4 030,00" for what our PO counts as 80 × 25kg bag
 *   at £50.375 (bill 43866, PO-26363). Without this, Add Bill read 1.00
 *   against 40 as a quantity to query, and "Update PO from bill" offered £400
 *   (or £2.015) as the bag's cost.
 *
 *   THE WAYS. Each turns the bill's quantity into the PO line's (× ratio) and
 *   its price the other way (÷ ratio); kg is the PO line's pack weight:
 *     tonnes → packs   × (1000 ÷ kg)   bill per tonne, PO in packs
 *     packs → tonnes   ÷ (1000 ÷ kg)   bill per pack, PO in tonnes
 *     kg → packs       ÷ kg            bill per kg, PO in packs
 *     kg → tonnes      ÷ 1000          bill per kg, PO in tonnes (a bulk line,
 *                                      or a pack item counted in tonnes)
 *   Only a PO line whose unit weight is known (the line's own unitWeightKg,
 *   else its item's; not None — utils/lineUnits) can differ; a tonne line
 *   (1000 kg) only kg → tonnes.
 *
 *   THE EVIDENCE — never a guess.
 *     1. With a PO COST, the PRICE decides: a way is taken when its converted
 *        price is nearer the PO line's cost than the price as billed is (by
 *        ratio), and within 25% of it — or within 50% when the quantity agrees
 *        too (a price rise and a unit change on one line). Several ways fitting
 *        (now there are four): the one sure on the price alone (25%) when it is
 *        the only such one, else the only one within 50% — otherwise nothing.
 *     2. With NO PO COST (0 / blank — Lacto's Xero-made POs, PO-26366), the
 *        QUANTITY alone decides: the converted quantity equals a PO quantity
 *        (ordered or supplied) to within UNIT_QTY_EXACT, and exactly one way
 *        fits. Never tonnes → packs on the quantity alone unless the invoice
 *        says tonnes: that way makes the bill's quantity BIGGER, and a part bill
 *        in the PO's own unit reads exactly like it (2 totes of 4, PO-19984).
 *        The other ways make it smaller — a bill for 25, 40 or 1000 times what
 *        was ordered is never a part bill. A near miss (2,010 kg against 80
 *        bags) converts nothing.
 *     3. Either way, the QUANTITY must not say otherwise: when the bill's
 *        quantity agrees with the PO's as it stands (within the PO unit's
 *        limit: 1 pack, or 0.5 T) and not converted, the two are in the same
 *        unit, whatever the price — a PO costed wrongly per tonne is not
 *        "confirmed" by it.
 *     4. THE INVOICE'S UNIT, when the reader gives one (billUnitOf: 'kg' / 't' /
 *        'pack' — services/billExtractionService reads the unit column; kept on
 *        the bill line as purchasing.bill_lines.unit): only the way FROM that
 *        unit may be taken, and a unit it does not know ('other' — litres, a
 *        pallet) converts nothing. It is evidence against, never a guess for —
 *        except that it is what lets tonnes → packs go on the quantity alone.
 *   The quantity agreeing is the PO line's ordered quantity or what was
 *   supplied (either).
 *
 *   THE COST it sets is what was paid over the converted quantity — the
 *   line's amount ÷ 40 bags — not the unit price × or ÷ the factor, which
 *   would carry the supplier's rounding of a per-pack price into a per-tonne
 *   one (PO-19284: 48 bags at £15.78 came to £757.20, i.e. £631.00 a tonne,
 *   not 15.78 × 40 = £631.20). The amount is used only when it is known and
 *   positive; else the price, converted. On a PO line with no cost that is
 *   the cost "Update PO from bill" offers — still the user's choice.
 *
 *   Pure numbers both sides hold: the screens (Add / Edit Bill, Bulk Add
 *   Bills) and the server (utils/billCostWriteBack.planCostWriteBack, the
 *   supplied copy in billService) decide alike — the invoice's unit included,
 *   because it is stored on the bill line (the reader gives it only once the
 *   column exists, so neither side has it before). The bill itself is never
 *   changed: it stays as the supplier invoiced it, and goes to Xero so. Only
 *   what the bill implies for the PO line — its quantity and its cost — is
 *   read in the PO line's unit: the quantity check, "Update PO from bill" (the
 *   PO's per-pack cost, the order line's cost less additives), and a copy of
 *   the bill's quantity into supplied.
 *
 *   1.1.0 (2026-10-14, Cam — "a Lacto Production bill … didn't seem to do
 *   anything"): kilograms (kg → packs, kg → tonnes), the quantity alone when
 *   the PO line has no cost, the invoice's unit, ambiguity refused; each
 *   answer now carries `ratio`, `billUnit`, `poUnit` and `evidence`.
 *
 *   1.2.0 (2026-10-15, Cam): a bill line may now be STORED in its PO line's
 *   quantity and unit when the user chooses it ("Use the PO's quantity and
 *   units", utils/billLineChoices) — its amount as invoiced, the figures as
 *   invoiced kept beside it (`as_billed`). keepReaderUnits also forgets those
 *   when the line's quantity or price is retyped: it is then a typed line.
 *
 *   1.3.0 (2026-10-22, Cam — "offer the user the option to convert the
 *   bill's units to match our PO … or keep the bill's units and change our
 *   PO's to match the supplier's … or keep both"): two cases production had
 *   that 1.2.0 missed, both a PO line COSTED IN THE WRONG UNIT, so the price
 *   could never decide:
 *     - NWF bill 44192: "1.00 T at £380" printed per tonne against PO-D10453
 *       2 × 500kg tote at £380 (the tote costed at the tonne's price). Now:
 *       when the price finds no way, THE INVOICE'S UNIT and the QUANTITY
 *       together decide — the way from the printed unit whose converted
 *       quantity equals a PO quantity exactly (evidence 'unit').
 *     - Mole Valley bill 44285: 8 bags at £11.69 against PO-D10626 0.2 (an
 *       item "in 25kg bags" whose unit weight says 1000 kg) at £11.69 — a bag
 *       bill against a PO line counted in tonnes. A tonne line's pack weight
 *       is read from its WORDS (`poWords`: "in 25kg bags", "500kg totes" —
 *       a weight followed by a pack word, one size only), and a bill quantity
 *       at least 20 times the PO's (packs or kg against tonnes) whose
 *       converted quantity is exactly the PO's counts on the quantity alone
 *       when the price finds no way (evidence 'quantity') — no part bill or
 *       double bill reads like that.
 *   Each answer now also says `packKg` (the pack size the bill or PO counts)
 *   and `poKg` — what the PO line's own weight is (1000 for a tonne line).
 *
 *   The server's utils/billUnitConversion.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { roundHalfUp, formatQuantity, formatCount } from './numberFormatters';
import { lineWeightKg } from './lineUnits';
import { unitLabelFor } from './units';

/** The converted price within this share of the PO cost: sure on the price alone. */
export const UNIT_PRICE_BAND = 0.25;
/** …or within this share, when the converted quantity agrees with the PO's too. */
export const UNIT_PRICE_BAND_WITH_QTY = 0.5;
/** How near a quantity must be to agree, in the PO line's unit (billQtyCheck's limits). */
export const UNIT_QTY_LIMITS = Object.freeze({ packs: 1, tonnes: 0.5 });
/** With no PO cost: how near the converted quantity must be to a PO quantity — exactly. */
export const UNIT_QTY_EXACT = 0.001;

export const TONNES_TO_PACKS = 'tonnes_to_packs';
export const PACKS_TO_TONNES = 'packs_to_tonnes';
export const KG_TO_PACKS = 'kg_to_packs';
export const KG_TO_TONNES = 'kg_to_tonnes';

const EPS = 0.0001;

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const r4 = (n) => roundHalfUp(n, 4);
const r2 = (n) => roundHalfUp(n, 2);

const KG_WORDS = /^(kg|kgs|kilo|kilos|kilogram|kilograms|kilogramme|kilogrammes)$/;
const TONNE_WORDS = /^(t|te|tn|tns|ton|tons|tonne|tonnes|mt)$/;
const PACK_WORDS = /\b(bag|bags|bigbag|bigbags|sac|sacs|sack|sacks|tote|totes|pack|packs|packet|packets|each|ea|unit|units|pce|pces|pcs|pc|piece|pieces)\b/;

/**
 * A bill line's unit as the invoice prints it, in one word: 'kg', 't'
 * (tonnes), 'pack' (a bag, sack, tote, "each"…), 'other' (something else —
 * litres, a pallet — which converts nothing), or null when none was read.
 * @param {string|null|undefined} raw — "KG", "Tonnes", "25kg bag", "sac", …
 * @returns {'kg'|'t'|'pack'|'other'|null}
 */
export function billUnitOf(raw) {
  if (raw === null || raw === undefined) return null;
  const w = String(raw).trim().toLowerCase().replace(/\.+$/, '').replace(/\s+/g, ' ');
  if (!w) return null;
  if (w === 'kg' || w === 't' || w === 'pack' || w === 'other') return w;
  if (KG_WORDS.test(w)) return 'kg';
  if (TONNE_WORDS.test(w)) return 't';
  if (PACK_WORDS.test(w.replace(/\bbig bag/g, 'bigbag'))) return 'pack';
  return 'other';
}

/**
 * Grid lines after a person's edit: a line whose quantity or price was
 * retyped loses the invoice's unit (`bill_unit`) — its figures are no longer
 * the invoice's, so the unit is no longer evidence of anything — and, if it
 * had been put in its PO line's units, the figures as invoiced with it
 * (`_asBilled`, utils/billLineChoices). Lines not retyped (and lines new to
 * the grid) are left as they are.
 * @param {object[]} prev — the lines before the edit
 * @param {object[]} next — the lines after it
 * @returns {object[]} next, or a copy with those units dropped
 */
export function keepReaderUnits(prev, next) {
  if (!Array.isArray(next)) return next;
  const before = new Map((Array.isArray(prev) ? prev : []).map(l => [l?.id, l]));
  const same = (a, b) => String(a ?? '') === String(b ?? '');
  let changed = false;
  const out = next.map(l => {
    if (!l?.bill_unit && !l?._asBilled) return l;
    const b = before.get(l.id);
    if (!b || (same(b.quantity, l.quantity) && same(b.unit_price, l.unit_price))) return l;
    changed = true;
    if (!l._asBilled) return { ...l, bill_unit: null };
    const choices = { ...(l._choices || {}) };
    delete choices.units;
    const kept = { ...(l._before || {}) };
    delete kept.units;
    return { ...l, bill_unit: null, _asBilled: null, _choices: choices, _before: kept };
  });
  return changed ? out : next;
}

/** The one unit a group of bill lines was billed in: null when none says, 'other' when they differ. */
function groupUnit(lines) {
  const units = [...new Set(lines.map(l => billUnitOf(l?.unit)).filter(Boolean))];
  if (!units.length) return null;
  return units.length === 1 ? units[0] : 'other';
}

/**
 * The cost a group of bill lines on ONE PO line sets: one line's price;
 * several at one price, that price; else what was actually paid — their
 * combined amount (line_amount, else qty × price) over their combined
 * quantity. With their combined quantity and amount, and — only when the
 * invoice said — the unit they were billed in (`unit`, billUnitOf).
 * @param {Array<{ quantity, unit_amount, line_amount?, unit? }>} lines
 * @returns {{ qty: number, cost: number|null, amount: number, unit?: string }}
 */
export function billGroupFigures(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const qty = list.reduce((s, l) => s + (num(l?.quantity) || 0), 0);
  const amount = list.reduce((s, l) => {
    const la = num(l?.line_amount);
    return s + (la !== null ? la : (num(l?.quantity) || 0) * (num(l?.unit_amount) || 0));
  }, 0);
  const prices = list.map(l => num(l?.unit_amount));
  let cost = null;
  if (list.length === 1) cost = prices[0];
  else if (list.length && prices.every(p => p !== null && r4(p) === r4(prices[0]))) cost = prices[0];
  else if (Math.abs(qty) > EPS) cost = amount / qty;
  const unit = groupUnit(list);
  return { qty: r4(qty) ?? 0, cost: cost === null ? null : r4(cost), amount: r2(amount) ?? 0, ...(unit ? { unit } : {}) };
}

/** Pack words a weight must be followed by to name a pack ("25kg bags", "500kg tote"). */
const PACK_AFTER_KG = /(\d+(?:\.\d+)?)\s*kgs?\s*(?:bag|bags|sack|sacks|sac|sacs|tote|totes|tub|tubs|bucket|buckets|pack|packs|bigbag|bigbags)\b/gi;

/**
 * The one pack size a tonne line's words name — "Elite Cattle Crunch in 25kg
 * bags" → 25 — or null (none, or more than one size: "2kg Deccox in 500kg
 * totes" names only 500, since 2kg is not followed by a pack word).
 * @param {string|null|undefined} words — the PO line's description (and / or its item's name)
 * @returns {number|null}
 */
export function packKgFromWords(words) {
  const text = String(words ?? '').replace(/big bag/gi, 'bigbag');
  const sizes = new Set();
  for (const m of text.matchAll(PACK_AFTER_KG)) {
    const kg = parseFloat(m[1]);
    if (Number.isFinite(kg) && kg >= 5 && kg < 1000) sizes.add(kg);
  }
  return sizes.size === 1 ? [...sizes][0] : null;
}

const agrees = (a, list, limit) => list.some(q => Math.abs(a - q) <= limit + EPS);

/**
 * The ways a bill may be in another unit than a PO line of this weight: how
 * the bill's quantity becomes the PO line's (× ratio), the unit on each side,
 * the factor said in words, and how near a quantity must be to agree.
 */
function waysFor(kg, wordsKg = null) {
  const ways = [];
  // A tonne line whose words name a pack: a bill in those packs.
  if (kg === 1000 && wordsKg > 0 && wordsKg !== 1000) {
    const f = 1000 / wordsKg;
    ways.push({ direction: PACKS_TO_TONNES, ratio: 1 / f, factor: f, from: 'pack', billUnit: 'packs', poUnit: 'tonnes', packKg: wordsKg });
  }
  if (kg !== 1000) {
    const f = 1000 / kg;
    ways.push({ direction: TONNES_TO_PACKS, ratio: f, factor: f, from: 't', billUnit: 'tonnes', poUnit: 'packs' });
    ways.push({ direction: PACKS_TO_TONNES, ratio: 1 / f, factor: f, from: 'pack', billUnit: 'packs', poUnit: 'tonnes' });
    ways.push({ direction: KG_TO_PACKS, ratio: 1 / kg, factor: kg, from: 'kg', billUnit: 'kg', poUnit: 'packs' });
  }
  ways.push({ direction: KG_TO_TONNES, ratio: 1 / 1000, factor: 1000, from: 'kg', billUnit: 'kg', poUnit: 'tonnes' });
  return ways.map(w => ({ ...w, packKg: w.packKg ?? kg, limit: UNIT_QTY_LIMITS[w.poUnit], kind: w.poUnit === 'packs' ? 'bags' : 'tonnes' }));
}

/**
 * The bill's figures for a PO line in the PO line's own unit, when the two
 * are counted differently — or null (the same unit, no evidence, or more than
 * one way fits).
 *
 * @param {object} p
 * @param {number|string} p.billQty      — the bill's quantity for the PO line (its lines added up)
 * @param {number|string} p.billCost     — the bill's unit price for it (billGroupFigures)
 * @param {number|string|null} [p.billAmount] — what the bill's lines for it come to, net
 * @param {string|null} [p.billUnit]     — the unit the invoice prints for it (billUnitOf), when read
 * @param {number|string} p.poCost       — the PO line's unit cost (unitAmount, as it stands or as
 *        edited); 0 / blank = none, and only the quantity can decide
 * @param {Array<number|string|null>|number|string} p.poQty — what the bill may agree with:
 *        the ordered quantity and, where recorded, what was supplied
 * @param {number|string|null} [p.lineWeight] — the PO line's own unitWeightKg (0 = None)
 * @param {number|string|null} [p.itemWeight] — its item's unit_weight_kg
 * @param {string|null} [p.poWords] — the PO line's words (description, item name): a tonne
 *        line's pack size is read from them ("in 25kg bags")
 * @returns {null|{
 *   direction: 'tonnes_to_packs'|'packs_to_tonnes'|'kg_to_packs'|'kg_to_tonnes',
 *   kg: number, factor: number, ratio: number,
 *   billQty: number, billCost: number, quantity: number, cost: number,
 *   qtyAgrees: boolean, kind: 'bags'|'tonnes',
 *   billUnit: 'tonnes'|'packs'|'kg', poUnit: 'packs'|'tonnes', evidence: 'price'|'quantity'|'unit',
 *   packKg: number, poKg: number, costIgnored?: boolean
 * }} quantity / cost — the bill's, in the PO line's unit (quantity = billQty ×
 *   ratio); kind — the PO line's unit as billQtyCheck counts it; kg / packKg —
 *   the pack size (1000 on a tonne line's kg way); poKg — the PO line's own
 *   weight; costIgnored — decided on the quantity although the PO has a cost
 *   (it looks to be in the other unit).
 */
export function billUnitConversion({ billQty, billCost, billAmount = null, billUnit = null, poCost, poQty, lineWeight = null, itemWeight = null, poWords = null } = {}) {
  const kg = lineWeightKg(lineWeight, itemWeight);
  if (!(kg > 0)) return null;
  const bq = num(billQty);
  const bc = num(billCost);
  if (bq === null || !(bq > 0) || !(bc > 0)) return null;
  const unit = billUnitOf(billUnit);
  if (unit === 'other') return null;
  const pc = num(poCost);
  const qtys = (Array.isArray(poQty) ? poQty : [poQty]).map(num).filter(q => q !== null);
  const ways = waysFor(kg, kg === 1000 ? packKgFromWords(poWords) : null).filter(w => !unit || w.from === unit);

  const result = ({ w, qty, qtyAgrees }, evidence) => {
    // What was paid over the converted quantity, where both are known.
    const amount = num(billAmount);
    const cost = amount > 0 && qty > EPS ? amount / qty : bc / w.ratio;
    return {
      direction: w.direction, kg: w.packKg, packKg: w.packKg, poKg: kg, factor: r4(w.factor), ratio: w.ratio,
      billQty: r4(bq), billCost: r4(bc),
      quantity: r4(qty), cost: r4(cost),
      qtyAgrees, kind: w.kind,
      billUnit: w.billUnit, poUnit: w.poUnit, evidence
    };
  };

  const found = [];
  if (pc > 0) {
    // The price decides: nearer the PO's cost than as billed, and near it.
    const asBilled = Math.abs(Math.log(bc / pc));
    for (const w of ways) {
      const qty = bq * w.ratio;
      const ratio = (bc / w.ratio) / pc;
      if (!(Math.abs(Math.log(ratio)) < asBilled)) continue;
      const qtyAgrees = agrees(qty, qtys, w.limit);
      if (!qtyAgrees && agrees(bq, qtys, w.limit)) continue;
      const off = Math.abs(ratio - 1);
      if (off <= UNIT_PRICE_BAND + EPS) found.push({ w, qty, qtyAgrees, sure: true });
      else if (qtyAgrees && off <= UNIT_PRICE_BAND_WITH_QTY + EPS) found.push({ w, qty, qtyAgrees, sure: false });
    }
    const sure = found.filter(f => f.sure);
    const pick = sure.length ? (sure.length === 1 ? sure[0] : null) : (found.length === 1 ? found[0] : null);
    if (pick) return result(pick, 'price');
    if (found.length) return null; // the price fits more than one way: never a guess
    // The price fits no way — the PO line may be costed in the wrong unit
    // (a tote at the tonne's price, PO-D10453; a 25kg-bag cost on a tonne
    // line, PO-D10626). The invoice's unit with an exact quantity, or a bill
    // 20+ times the PO's quantity converting exactly to it (1.3.0).
    return exactQuantity(unit ? 'unit' : 'big');
  }

  // No PO cost: the quantity alone — exactly, by one way only.
  return exactQuantity('quantity');

  function exactQuantity(mode) {
    if (!qtys.some(q => q > 0)) return null;
    const hits = [];
    for (const w of ways) {
      // Tonnes → packs makes the bill's quantity bigger: a part bill reads the
      // same, so only when the invoice says tonnes.
      if (w.ratio > 1 && unit !== 't') continue;
      // With a PO cost and no printed unit: only a bill 20+ times the PO's.
      if (mode === 'big' && !(w.ratio <= 1 / 20 + EPS)) continue;
      const qty = bq * w.ratio;
      if (!qtys.some(q => q > 0 && Math.abs(qty - q) <= UNIT_QTY_EXACT)) continue;
      // The bill's quantity as it stands agreeing says one unit — except
      // that a printed unit is evidence of its own: then only an EXACT match
      // as it stands says so (1 T against 2 totes is not "within a tote").
      if (mode === 'unit' ? qtys.some(q => Math.abs(bq - q) <= UNIT_QTY_EXACT) : agrees(bq, qtys, w.limit)) continue;
      hits.push({ w, qty, qtyAgrees: true });
    }
    if (hits.length !== 1) return null;
    const out = result(hits[0], mode === 'unit' ? 'unit' : 'quantity');
    return mode === 'quantity' ? out : { ...out, costIgnored: true };
  }
}

/** A PO-unit quantity back in the bill's unit (for "Use PO quantities"). */
export function toBillUnits(conv, poUnitQty) {
  const q = num(poUnitQty);
  if (!conv || q === null) return q;
  const ratio = conv.ratio || (conv.direction === TONNES_TO_PACKS ? 1000 / conv.kg : conv.kg / 1000);
  return r4(q / ratio);
}

const tonnesText = (t) => `${formatQuantity(t)} T`;
const kgText = (k) => `${formatQuantity(k)} kg`;
const packsText = (n, kg) => `${formatCount(n)} × ${unitLabelFor(kg)}`;
const money = (v) => `£${(r4(v) ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const sideText = (conv, side, q) => (side === 'kg' ? kgText(q) : side === 'tonnes' ? tonnesText(q) : packsText(q, conv.kg));
/** "a tonne", "a kg", "a 25kg bag". */
const perUnit = (conv, side) => (side === 'kg' ? 'a kg' : side === 'tonnes' ? 'a tonne' : `a ${unitLabelFor(conv.kg)}`);
/** "25kg bags", "500kg totes", "12.5kg packs". */
const packsPlural = (kg) => {
  const label = unitLabelFor(kg);
  return /(bag|tote)$/.test(label) ? `${label}s` : `${label} packs`;
};

/** "1.00 T = 40 × 25kg bag", "48 × 25kg bag = 1.20 T", "2,000.00 kg = 80 × 25kg bag". */
export function conversionQtyText(conv) {
  if (!conv) return '';
  const billSide = conv.billUnit || (conv.direction === TONNES_TO_PACKS ? 'tonnes' : 'packs');
  const poSide = conv.poUnit || (conv.direction === TONNES_TO_PACKS ? 'packs' : 'tonnes');
  return `${sideText(conv, billSide, conv.billQty)} = ${sideText(conv, poSide, conv.quantity)}`;
}

/** "£400.00 a tonne = £10.00 a 25kg bag", "£2.015 a kg = £50.375 a 25kg bag", … */
export function conversionCostText(conv) {
  if (!conv) return '';
  const billSide = conv.billUnit || (conv.direction === TONNES_TO_PACKS ? 'tonnes' : 'packs');
  const poSide = conv.poUnit || (conv.direction === TONNES_TO_PACKS ? 'packs' : 'tonnes');
  return `${money(conv.billCost)} ${perUnit(conv, billSide)} = ${money(conv.cost)} ${perUnit(conv, poSide)}`;
}

/** The whole of it, in one sentence for a line or a window. */
export function conversionText(conv) {
  if (!conv) return '';
  const how = {
    [TONNES_TO_PACKS]: 'Billed per tonne, PO in packs',
    [PACKS_TO_TONNES]: 'Billed per pack, PO in tonnes',
    [KG_TO_PACKS]: `Billed per kg, PO in ${packsPlural(conv.kg)}`,
    [KG_TO_TONNES]: 'Billed per kg, PO in tonnes',
  }[conv.direction] || 'Billed in another unit';
  const wrongCost = ' — the PO line\'s cost looks to be in the other unit';
  const why = conv.evidence === 'quantity'
    ? (conv.costIgnored ? ` (its quantity converts exactly to the PO's${wrongCost})` : ' (the PO line has no cost — its quantity matches exactly)')
    : conv.evidence === 'unit' ? ` (the invoice prints the unit, and its quantity converts exactly to the PO's${wrongCost})` : '';
  return `${how}: ${conversionQtyText(conv)}; ${conversionCostText(conv)}${why}.`;
}

/**
 * Does the bill's line total agree with what the PO line comes to on the same
 * quantity (Jade: "just check totals match per line")? The PO line at its own
 * cost on the quantity compared (supplied, else ordered), to the penny.
 * @returns {{ billAmount: number, poAmount: number, diff: number, agrees: boolean }}
 */
export function lineTotalsCheck({ billAmount, poQty, poCost }) {
  const b = r2(num(billAmount) || 0);
  const p = r2((num(poQty) || 0) * (num(poCost) || 0));
  const diff = r2(b - p);
  return { billAmount: b, poAmount: p, diff, agrees: Math.abs(diff) < 0.005 };
}

/**
 * The per-line totals check in words: "Line £400.00 — the PO line comes to
 * the same on the supplied quantity." / "… comes to £380.00 on the supplied
 * quantity, £20.00 apart."
 * @param {ReturnType<typeof lineTotalsCheck>} t
 * @param {'supplied'|'ordered'} [basis]
 */
export function lineTotalsText(t, basis = 'ordered') {
  if (!t) return '';
  const on = basis === 'supplied' ? 'the supplied quantity' : 'the ordered quantity';
  const pounds = (v) => `£${(r2(v) ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return t.agrees
    ? `Line ${pounds(t.billAmount)} — the PO line comes to the same on ${on}.`
    : `Line ${pounds(t.billAmount)} — the PO line comes to ${pounds(t.poAmount)} on ${on}, ${pounds(Math.abs(t.diff))} apart.`;
}
